import dayjs, { UnitTypeLong } from 'dayjs';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useUser } from 'src/UserContext';
import { userProductsCreated } from 'src/services/api';
import useCustomHistory from 'src/hooks/useCustomHistory';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import { PRODUCT_CATEGORY, USER_PRODUCT_STATUS } from 'src/constants/product';
import { KY_BIXBY_NAME, KY_KARAOKE_NAME, KY_PREFIX_KARAOKE, KY_PREFIX_BIXBY } from 'src/constants/ky';
import { calcRemainDay, getTranslateName } from 'src/services/util';

type ProductGuideParams = {
  userProducts: any;
  goPurchase: () => void;
  goMyProduct: (category: string) => void;
};
function ProductGuide({ userProducts, goPurchase, goMyProduct }: ProductGuideParams) {
  const { t } = useTranslation();

  if (Array.isArray(userProducts) && !!userProducts.length) {
    const items = userProducts.map((item, index) => {
      const isBixbyProduct = item.category.toLowerCase() === 'bixby';
      const calcData = calcRemainDay(item.endDate);
      return (
        <div
          key={index}
          onClick={() => {
            goMyProduct(isBixbyProduct ? PRODUCT_CATEGORY.BIXBY : PRODUCT_CATEGORY.KARAOKE);
          }}
          className={classNames(
            'cursor-pointer',
            'pt-4',
            'pb-5',
            'px-6',
            'flex',
            'flex-row',
            'justify-between',
            'items-center',
            'border',
            'border-ice-blue',
            'rounded',
            {
              'mt-2': index > 0,
            },
          )}
        >
          <div className="flex flex-col items-start">
            <div className="flex items-center">
              <div className="N_12_R text-greyish-brown">{isBixbyProduct ? KY_PREFIX_BIXBY : KY_PREFIX_KARAOKE}</div>
              <div className="division mx-1.5"></div>
              <div className="N_12_R text-greyish-brown">{isBixbyProduct ? KY_BIXBY_NAME : KY_KARAOKE_NAME}</div>
            </div>
            <div className="mt-2.5 N_14_M">{item[getTranslateName()]}</div>
            {USER_PRODUCT_STATUS.SUB_CREATED === item.status ? (
              <div className="N_12_R text-brown-grey mt-0.5">{t('product/my/sub_created_message')}</div>
            ) : (
              <div className="flex items-center mt-0.5">
                <div className="N_12_R text-brown-grey">
                  <Trans
                    i18nKey="home/greeting_date"
                    values={{
                      date: dayjs(item.endDate).format(calcData.format),
                    }}
                  ></Trans>
                </div>
                <span className="mx-1.5 oval-2" />
                <div className="N_12_R text-brown-grey">
                  <Trans
                    i18nKey={`home/greeting_remain_${calcData.translateName}`}
                    values={{ date: dayjs(item.endDate).diff(dayjs(), calcData.dayjsName as UnitTypeLong) }}
                  ></Trans>
                </div>
              </div>
            )}
          </div>
          <div className="">
            <Icon name={isBixbyProduct ? 'logo_bixby' : 'logo_karaoke'} size="32" />
          </div>
        </div>
      );
    });
    return <div>{items}</div>;
  } else {
    return (
      <div className="p-5 border border-ice-blue rounded">
        <p className="mt-4 N_14_R text-brown-grey text-center">
          <Trans i18nKey="home/greeting_no_product"></Trans>
        </p>
        <div className="mt-7">
          <Button title={t('home/greeting_purchase_button_title')} theme="sub" onClick={goPurchase} />
        </div>
      </div>
    );
  }
}

export default function UserInfo(): JSX.Element {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const [userEmail, setUserEmail] = useState('');
  const [userProducts, setUserProducts] = useState<CheckoutUserProduct[]>([]);
  const { isInit, isLogin, userInfo } = useUser();
  useEffect(() => {
    if (!isInit) {
      return;
    }
    if (isLogin) {
      setUserEmail(userInfo && userInfo.email ? userInfo.email : '');
      (async () => {
        try {
          const karaokeProducts = await userProductsCreated();
          const bixbyProducts = await userProductsCreated({
            category: PRODUCT_CATEGORY.BIXBY,
          });
          setUserProducts(karaokeProducts.products.concat(bixbyProducts.products));
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [isInit, isLogin, userInfo]);

  return (
    <>
      {userEmail ? (
        <div>
          <p className="N_16_R text-black">
            <Trans
              i18nKey="home/greeting_login_user"
              components={{ span: <span className="N_16_M text-bluey-purple" /> }}
              values={{ email: userEmail }}
            ></Trans>
          </p>
          <div className="mt-5">
            <ProductGuide
              userProducts={userProducts}
              goPurchase={() => {
                history.push('/product/purchase');
              }}
              goMyProduct={(category: string) => {
                history.push('/product/my', {
                  tab: category,
                });
              }}
            />
          </div>
        </div>
      ) : (
        <div className="pb-2">
          <p className="N_16_R text-black">
            <Trans i18nKey="home/greeting_no_login_user"></Trans>
          </p>
          <div className="mt-7">
            <Button
              title={t('home/greeting_login_button_title')}
              theme="sub"
              onClick={(e) => {
                e.preventDefault();
                history.push('/login');
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
